import { useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

/**
 * Mapa de segmentos de rota → rótulo humano em Português.
 * Chaves genéricas cobrem os principais módulos do Hermes.
 */
const SEGMENT_LABELS = {
  // Núcleo
  dashboard: "Início",
  profile: "Perfil",
  settings: "Configurações",
  notificacoes: "Notificações",
  // Clientes e documentos
  clients: "Clientes",
  reader: "Leitor",
  review: "Revisão OCR",
  documents: "Documentos",
  templates: "Modelos & Cláusulas",
  "doc-templates": "Modelos DOCX",
  "modelos-converge": "Modelos IA",
  "new-document": "Novo documento",
  contrato: "Contrato",
  procuracao: "Procuração",
  // Escritório
  kanban: "Escritório",
  // Prazos
  prazos: "Prazos",
  radar: "Radar",
  // Preparados
  preparado: "Preparado Hermes",
  preparados: "Preparados",
  comparar: "Comparar",
  // Jurisprudência & Inteligência
  jurisprudencia: "Jurisprudência",
  inteligencia: "Inteligência 360",
  pedidos: "Pedidos",
  watches: "Monitores",
  legislativo: "Legislativo",
  contratacoes: "Contratações",
  "radar-mineral": "Radar Mineral",
  // DJEN & processos
  djen: "DJEN",
  processos: "Processos",
  // Financeiro
  finance: "Financeiro",
  cobrancas: "Cobranças",
  plans: "Planos",
  "meu-plano": "Meu Plano",
  payment: "Pagamento",
  success: "Sucesso",
  cancel: "Cancelado",
  // Cálculos
  calculos: "Cálculos",
  atualizacao: "Atualização de Sentença",
  "liquidacao-trabalhista": "Liquidação Trabalhista",
  nova: "Nova",
  parametros: "Parâmetros",
  titulo: "Título",
  calculo: "Cálculo",
  // Referrals & outros
  indicacoes: "Indicações",
  "vale-a-pena": "Vale a pena?",
  jogos: "Jogos",
  noticias: "Notícias",
  mineral: "Mineral",
  extrato: "Extrato",
  // Admin
  admin: "Admin",
  users: "Usuários",
  coupons: "Cupons",
  audit: "Auditoria",
  geo: "Distribuição geográfica",
  birthdays: "Aniversariantes",
  financial: "Financeiro",
  analytics: "Analytics",
  communications: "Comunicações",
  intelligence: "Inteligência 360",
  "creditos-mineral": "Créditos Mineral",
  images: "Imagens",
  comercial: "Comercial",
  apis: "APIs & Health-check",
};

/**
 * Segmentos identificados como IDs (números, UUIDs, CNJ ~20 dígitos) recebem rótulo genérico.
 */
function labelFor(segment, prev) {
  if (!segment) return "";
  // ID numérico ou CNJ (com pontos/hifens)
  const digitsOnly = segment.replace(/[.\-\/]/g, "");
  if (/^[0-9]+$/.test(digitsOnly) && digitsOnly.length >= 4) {
    if (prev === "processos") return "Processo";
    if (prev === "clients") return "Cliente";
    if (prev === "documents") return "Documento";
    if (prev === "preparado") return "Preparado";
    if (prev === "liquidacao-trabalhista") return "Liquidação";
    return "Detalhes";
  }
  // UUID
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
    return "Detalhes";
  }
  return SEGMENT_LABELS[segment] || segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Breadcrumbs — Visual only (não clicáveis).
 * Aparece APENAS quando a rota tem profundidade ≥ 2 segmentos.
 * Ex.: /prazos/kanban → "Início / Prazos / Escritório"
 *      /inteligencia/radar → "Início / Inteligência 360 / Radar"
 *      /admin/apis → "Início / Admin / APIs & Health-check"
 */
export default function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);

  // Rotas rasas (≤ 1 segmento) não exibem breadcrumbs
  if (parts.length < 2) return null;

  const crumbs = [];
  crumbs.push({ label: "Início", isHome: true });
  for (let i = 0; i < parts.length; i++) {
    const label = labelFor(parts[i], parts[i - 1]);
    if (!label) continue;
    crumbs.push({ label });
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className="hidden md:flex items-center gap-1.5 text-[11.5px] text-slate-500 font-sans"
      data-testid="hermes-breadcrumbs"
    >
      {crumbs.map((c, idx) => {
        const isLast = idx === crumbs.length - 1;
        return (
          <span key={idx} className="flex items-center gap-1.5">
            {idx > 0 && <ChevronRight className="w-3 h-3 text-slate-300" strokeWidth={2.5} />}
            {c.isHome && <Home className="w-3 h-3 text-slate-400" />}
            <span
              className={isLast ? "text-[#0E1D2D] font-semibold" : "text-slate-500"}
              data-testid={isLast ? "breadcrumb-current" : undefined}
            >
              {c.label}
            </span>
          </span>
        );
      })}
    </nav>
  );
}
